/**
 * Videos the question box has mentioned lately, so that the context selector can
 * flag them when it offers them again. The counterpart of
 * {@link ./RecentAccountCompletions} for videos.
 *
 * Only the id is kept: everything else about a video (title, and especially its
 * presigned thumbnail URL, which outlives an hour) is fetched fresh, so a stored
 * copy would go stale long before the entry expired.
 */

type RecentVideoMention = {
	id: string;
	timestamp: string;
};

const storageKey = "RecentVideoMentions";
const expiryMs = 7 * 24 * 60 * 60 * 1000;

function readEntries(): RecentVideoMention[] {
	try {
		const raw = localStorage.getItem(storageKey);
		if (!raw) return [];
		return JSON.parse(raw) as RecentVideoMention[];
	} catch {
		return [];
	}
}

function writeEntries(entries: RecentVideoMention[]): void {
	localStorage.setItem(storageKey, JSON.stringify(entries));
}

function pruneExpired(entries: RecentVideoMention[]): RecentVideoMention[] {
	const cutoff = Date.now() - expiryMs;
	return entries.filter((e) => Date.parse(e.timestamp) > cutoff);
}

/**
 * Records a video the question has just mentioned. Recency belongs to the video
 * rather than to the clip: taking 0:10–0:20 of a video makes that video recent,
 * however it is offered next time.
 */
export function saveRecentVideoMention(videoId: string): void {
	if (videoId.length === 0) return;
	const entries = pruneExpired(readEntries());
	const deduped = entries.filter((e) => e.id !== videoId);
	writeEntries([
		{ id: videoId, timestamp: new Date().toISOString() },
		...deduped,
	]);
}

export function isRecentVideoMention(videoId: string): boolean {
	return pruneExpired(readEntries()).some((e) => e.id === videoId);
}
